import { useCallback, useEffect, useRef, useState } from 'react';
import { useConversationSearch } from '../hooks/useConversationSearch';
import { useMenuOpen } from '../hooks/useMenuOpen';
import { useResponsive } from '../hooks/useResponsive';
import { useThemeTemplates } from '../hooks/useThemeTemplates';
import SkeletonSummary from './skeleton/skeletonSummary';
import NotesSkeletonLoader from './skeleton/notesSkeletonLoader';
import Results from './results/results';
import SearchForm from './searchForm/searchForm';
import Trending from './trending/trending';
import ThemeHeader from './header/ThemeHeader';
import ThemeFooter from './footer/ThemeFooter';
import ScriptInjector from './scriptInjector/ScriptInjector';
import './skeleton/skeletonLoader.sass';

export default function BuscadorApp() {
  const { theme } = useThemeTemplates();
  const {
    conversation,
    showLoading,
    isSearching,
    storageWasEmpty,
    search,
    clearConversation,
    startNewConversation,
  } = useConversationSearch();
  
  // Obtener la longitud de hits de la última búsqueda para calcular initialResults
  const lastTurn = conversation[conversation.length - 1];
  const hitsLength = lastTurn?.hits?.length || null;
  const { isMobile, initialResults } = useResponsive(hitsLength);
  
  const [query, setQuery] = useState('');
  const [searchParamsChanged, setSearchParamsChanged] = useState(false);
  // Cómo llegó la búsqueda (escrita, sugerencia o url): va al tracking del agente.
  const origenRef = useRef('escrita');
  const [headerReady, setHeaderReady] = useState(false);
  const [footerReady, setFooterReady] = useState(false);
  const handleHeaderReady = useCallback(() => setHeaderReady(true), []);
  const handleFooterReady = useCallback(() => setFooterReady(true), []);
  const isMenuOpen = useMenuOpen();

  const handleNewConversation = useCallback(() => {
    startNewConversation();
    setQuery('');

    const url = new URL(window.location.href);
    url.searchParams.delete('q');
    window.history.replaceState({}, '', url.toString());
  }, [startNewConversation]);

  const handleSearchSubmit = useCallback((nextQuery, origen = 'escrita') => {
    const trimmedQuery = nextQuery.trim();
    if (!trimmedQuery || isSearching) return;

    origenRef.current = origen;
    setQuery(trimmedQuery);
    setSearchParamsChanged(true);
  }, [isSearching]);

  // Lee el parámetro ?q= de la URL (enlace compartido) en la carga inicial.
  useEffect(() => {
    if (query) return;

    const urlParams = new URLSearchParams(window.location.search);
    const initialQuery = urlParams.get('q') || '';

    if (initialQuery === 'new-conversation') {
      handleNewConversation();
      return;
    }

    if (!initialQuery.trim()) return;

    origenRef.current = 'url';
    setQuery(initialQuery);
    setSearchParamsChanged(true);
  }, [query, handleNewConversation]);

  // Sincroniza atrás/adelante del navegador con la búsqueda visible.
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const nextQuery = urlParams.get('q');

      if (!nextQuery) {
        setQuery('');
        clearConversation();
        return;
      }

      origenRef.current = 'url';
      setQuery(nextQuery);
      setSearchParamsChanged(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [clearConversation]);

  // Dispara la búsqueda solo cuando el query proviene de la URL o del formulario,
  // y luego limpia el querystring para no persistir el valor q.
  useEffect(() => {
    if (!searchParamsChanged) return;

    setSearchParamsChanged(false);
    search(query, { origen: origenRef.current });

    const url = new URL(window.location.href);
    if (url.searchParams.has('q')) {
      url.searchParams.delete('q');
      window.history.replaceState({}, '', url.toString());
    }
  }, [query, search, searchParamsChanged]);

  // El botón social del template conserva la acción de iniciar una conversación.
  useEffect(() => {
    const handleSocialCunoClick = (event) => {
      const target = event.target.closest('.social-share-float .social-item.cuno');
      if (target) handleNewConversation();
    };

    document.addEventListener('click', handleSocialCunoClick);
    return () => document.removeEventListener('click', handleSocialCunoClick);
  }, [handleNewConversation]);

  const showFullSkeleton = showLoading && query && conversation.length === 0 && storageWasEmpty;
  const hasConversation = conversation.length > 0;
  const isSearchActive = hasConversation || showFullSkeleton;

  return (
    <>
      {isMenuOpen && <div id="body-overlay" />}
      <ThemeHeader header={theme.header} onReady={handleHeaderReady} />

      <main className={`grid-layout${isSearchActive ? ' has-results' : ''}`}>
        <div className="container-cuno-search">
          {!isSearchActive && (
            <>
              <div className="container-user-search">
                <h1>Hola, ¿Cómo puedo ayudarte?</h1>
              </div>
              <SearchForm
                onSubmit={handleSearchSubmit}
                isSearching={isSearching}
                isSearchActive={isSearchActive}
              />
            </>
          )}

          {showFullSkeleton && (
            <div className="results-container">
              <div className="container-full-results">
                <div className="conversation-row">
                  <SkeletonSummary />
                  <NotesSkeletonLoader
                    count={initialResults}
                    initialResults={initialResults}
                    isMobile={isMobile}
                  />
                </div>
              </div>
            </div>
          )}

          {hasConversation && (
            <Results
              conversation={conversation}
              isMobile={isMobile}
              initialResults={initialResults}
              onNewConversation={handleNewConversation}
            />
          )}

          {!hasConversation && !query && (
            <Trending onSearch={handleSearchSubmit} isSearching={isSearching} />
          )}
        </div>
      </main>

      {isSearchActive && (
        <SearchForm
          onSubmit={handleSearchSubmit}
          isSearching={isSearching}
          isSearchActive={isSearchActive}
        />
      )}

      <ThemeFooter footer={theme.footer} onReady={handleFooterReady} />
      {headerReady && footerReady && <ScriptInjector script={theme.script} />}
    </>
  );
}
